/**
 * Currency Utilities for US Dollar formatting and input handling
 * Optimized
 */

/**
 * Format a number as currency for display
 * @param amount - The amount to format
 * @param currency - The currency code (default: USD)
 * @param options - Additional formatting options
 * @returns Formatted currency string (e.g., "$50,000")
 */
export function formatCurrency(
  amount: number, 
  currency: string = "USD",
  options: {
    minimumFractionDigits?: number;
    maximumFractionDigits?: number;
    showCents?: boolean;
  } = {}
): string {
  const {
    minimumFractionDigits = 0,
    maximumFractionDigits = 0,
    showCents = false
  } = options;

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
    minimumFractionDigits: showCents ? 2 : minimumFractionDigits,
    maximumFractionDigits: showCents ? 2 : maximumFractionDigits,
  }).format(amount);
}

/**
 * Format a number as currency for input display (with commas)
 * @param amount - The amount to format
 * @returns Formatted string for input display (e.g., "50,000")
 */
export function formatCurrencyInput(amount: number): string {
  if (isNaN(amount) || amount === 0) return "";
  
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);
}

/**
 * Parse currency input string to numeric value
 * @param input - The input string (e.g., "$50,000", "50000", "50,000")
 * @returns Numeric value
 */
export function parseCurrencyInput(input: string): number {
  if (!input || typeof input !== "string") return 0;
  
  // Remove currency symbols, commas, and whitespace
  const cleaned = input.replace(/[$,\s]/g, "");
  
  // Parse to float
  const parsed = parseFloat(cleaned);
  
  return isNaN(parsed) ? 0 : parsed;
}

/**
 * Validate currency input
 * @param input - The input to validate
 * @param options - Validation options
 * @returns Validation result
 */
export function validateCurrencyInput(
  input: string,
  options: {
    required?: boolean;
    min?: number;
    max?: number;
    allowZero?: boolean;
  } = {}
): {
  isValid: boolean;
  error?: string;
  value?: number;
} {
  const { required = false, min, max, allowZero = true } = options;

  // Check if required
  if (required && (!input || input.trim() === "")) {
    return {
      isValid: false,
      error: "Amount is required"
    };
  }

  // If empty and not required, it's valid
  if (!input || input.trim() === "") {
    return {
      isValid: true,
      value: 0
    };
  }

  // Parse the value
  const numericValue = parseCurrencyInput(input);

  // Check if it's a valid number
  if (isNaN(numericValue)) {
    return {
      isValid: false,
      error: "Invalid amount format"
    };
  }

  // Check if zero is allowed
  if (!allowZero && numericValue === 0) {
    return {
      isValid: false,
      error: "Amount must be greater than zero"
    };
  }

  // Check minimum value
  if (min !== undefined && numericValue < min) {
    return {
      isValid: false,
      error: `Amount must be at least ${formatCurrency(min)}`
    };
  }

  // Check maximum value
  if (max !== undefined && numericValue > max) {
    return {
      isValid: false,
      error: `Amount cannot exceed ${formatCurrency(max)}`
    };
  }

  return {
    isValid: true,
    value: numericValue
  };
}

/**
 * Format currency for storage (as string number)
 * @param input - The input value
 * @returns String representation of the number for storage
 */
export function formatCurrencyForStorage(input: string | number): string {
  const numericValue = typeof input === "string" ? parseCurrencyInput(input) : input;
  return numericValue > 0 ? numericValue.toString() : "";
}

/**
 * Custom hook for currency input handling
 */
export function useCurrencyInput(onChange: (value: string) => void) {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    
    // Remove any non-digit characters except decimal point
    const cleaned = inputValue.replace(/[^\d.]/g, "");
    
    // Ensure only one decimal point
    const parts = cleaned.split(".");
    const sanitized = parts.length > 2 
      ? parts[0] + "." + parts.slice(1).join("")
      : cleaned;

    // Update with the sanitized value
    onChange(sanitized);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Allow: backspace, delete, tab, escape, enter, home, end, left, right arrows
    if (
      [
        "Backspace",
        "Tab",
        "Escape", 
        "Enter",
        "Delete",
        "Home",
        "End",
        "ArrowLeft",
        "ArrowRight",
      ].includes(e.key) ||
      // Allow Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
      (e.key === "a" && e.ctrlKey) ||
      (e.key === "c" && e.ctrlKey) ||
      (e.key === "v" && e.ctrlKey) ||
      (e.key === "x" && e.ctrlKey)
    ) {
      return;
    }

    // Allow digits and decimal point
    if (
      (e.key >= "0" && e.key <= "9") ||
      (e.key >= "Numpad0" && e.key <= "Numpad9") ||
      e.key === "."
    ) {
      return;
    }

    e.preventDefault();
  };

  return {
    onChange: handleChange,
    onKeyDown: handleKeyDown,
  };
}

/**
 * Predefined currency validation patterns for different contexts
 */
export const CURRENCY_VALIDATION_PATTERNS = {
  // Income validation (no upper limit for most cases)
  income: {
    min: 0,
    max: 10000000, // $10M max
    allowZero: true,
  },
  
  // Premium validation (reasonable insurance premium range)
  premium: {
    min: 1,
    max: 100000, // $100K max annual premium
    allowZero: false,
  },
  
  // Coverage amount validation (life insurance coverage)
  coverage: {
    min: 1000,
    max: 10000000, // $10M max coverage
    allowZero: false,
  },
  
  // Net worth validation
  netWorth: {
    min: 0,
    max: 100000000, // $100M max
    allowZero: true,
  }
} as const;

/**
 * Format currency amounts for different contexts
 */
export const CURRENCY_DISPLAY_FORMATS = {
  // For large amounts, show without cents
  large: { showCents: false, maximumFractionDigits: 0 },
  
  // For precise amounts, show with cents
  precise: { showCents: true, minimumFractionDigits: 2 },
  
  // For input placeholders
  placeholder: { showCents: false, maximumFractionDigits: 0 }
} as const;